/**
 * One customer's billing: the work timer, time and parts (each editable until it is invoiced), the
 * rate rules that price them, and the invoices generated from them.
 *
 * The port of `Views/BillingPage.xaml` + `BillingViewModel`; the date and wording rules are in
 * `billing.ts`, under test.
 *
 *   * EVERYTHING BILLABLE HANGS OFF A TICKET. Every write needs one picked, so the refusal is a
 *     disabled button rather than a round trip; the server refuses anyway, by name.
 *   * THE TIMER AND THE TIME EDITOR SHARE THE TICKET - two ways of logging the same work - but not the
 *     note or the Billable tick, unlike the XAML: there one note box fed both, so editing a manual
 *     entry rewrote the running timer's note. A blank note at Stop keeps the one typed at Start.
 *   * A refresh after a save KEEPS the picked tickets and catalog item; rebuilding the lists would clear
 *     a choice made moments ago, and a refresh runs after every save.
 *   * HELD WORK IS A STANDING SENTENCE, worked out from the rows on screen, not only the answer to a
 *     Generate that has already come back empty - and that refusal names the tickets as the server
 *     wrote it.
 *   * Picking a catalog item prefills description, cost and THIS customer's price (with which rule set
 *     it); a markup-derived price is left blank, because blank means "work it out at invoice time" and
 *     typing the number in would freeze it. Loading a stored charge never re-quotes.
 *   * Each editor reports on its own line - the Phase 4 rule: an outcome goes where the section is.
 */
import type { Container } from 'webwin';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import {
  heldWorkHint,
  localTimeOfDay,
  rateRulesLines,
  sortTickets,
  startUtc,
  ticketLabel,
  timerLabel
} from '../billing.js';
import {
  CatalogPriceSource,
  RateKind,
  type CatalogItemDto,
  type InvoiceDto,
  type PurchaseDto,
  type TicketDto,
  type TimeEntryDto,
  type WorkTimerDto
} from '../contracts.gen.js';
import { join, localDate, money } from '../format.js';
import { localIsoOf, localNoonUtc, todayLocalIso } from '../invoicing.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { dateBox } from '../ui/DateBox.js';
import { confirm } from '../ui/dialogs.js';
import { moneyBox } from '../ui/MoneyBox.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkLine,
  comboBox,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack,
  wrappingCheck
} from '../ui/layout.js';
import { invoiceCard } from './sections/invoice-card.js';
import type { CrmPage, PageContext } from './page.js';

const PICK_TICKET = 'Pick a ticket';
const NO_CATALOG = 'Not from the catalog';

export function billingPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      const customerId = Number(ctx.params.id);
      const { view, content } = scrollPage();
      host.controls.add(view);

      let tickets: TicketDto[] = [];
      let catalog: CatalogItemDto[] = [];
      let entries: TimeEntryDto[] = [];
      let purchases: PurchaseDto[] = [];
      let timer: WorkTimerDto | null = null;
      let editingTimeId: number | null = null;
      let editingPurchaseId: number | null = null;
      let quiet = false; // set while the page moves a picker itself, so it does not act on its own change

      const title = h1('Billing');
      const pageStatus = new StatusLine();
      content.controls.add(
        stackOf(
          vstack({ spacing: GAP.sm }),
          stackOf(buttonRow(), button('← Customer', () => ctx.go(`/customers/${customerId}`))),
          title,
          pageStatus.label
        )
      );

      // ---- Rate rules ----
      const rateLine1 = text('');
      const rateLine2 = muted('');
      const rates = card();
      rates.controls.add(stackOf(vstack({ spacing: GAP.sm }), h2('Rate rules'), rateLine1, rateLine2));

      const noTickets = card({ tint: 'info', visible: false });
      noTickets.controls.add(text('This customer has no tickets. Raise one first - time and charges bill under a ticket.'));
      const heldCard = card({ tint: 'warn', visible: false });
      const heldText = text('');
      heldCard.controls.add(heldText);

      // ---- Work timer ----
      const timerText = text('', { font: FONT.bold });
      const timerTicket = comboBox<string>([PICK_TICKET]);
      const timerNote = textBox({ placeholder: 'What you are working on' });
      const timerBillable = wrappingCheck('Billable', true);
      const timerStatus = new StatusLine();
      const startButton = button('Start', () => void startTimer(), true);
      const stopButton = button('Stop & log', () => void stopTimer(), true);
      const discardButton = button('Discard', () => void discardTimer());
      const timerCard = card();
      timerCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Work timer'),
          timerText,
          field('Ticket', timerTicket),
          field('Note', timerNote),
          checkLine(timerBillable),
          stackOf(buttonRow(), startButton, stopButton, discardButton),
          timerStatus.label
        )
      );

      // ---- Time entry ----
      const timeTicket = comboBox<string>([PICK_TICKET]);
      const timeDate = dateBox({ iso: todayLocalIso() });
      const timeStart = textBox({ placeholder: 'HH:mm' });
      const timeMinutes = textBox({ text: '30' });
      const timeRate = moneyBox({ placeholder: '0.00' });
      const timeBillable = wrappingCheck('Billable', true);
      const timeNote = textBox();
      const timeStatus = new StatusLine();
      const timeSave = button('Add time', () => void saveTime(), true);
      const timeCancel = button('Cancel', () => resetTimeEditor());
      timeCancel.visible = false;
      const timeCard = card();
      timeCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Time entry'),
          field('Ticket', timeTicket),
          fieldRow(field('Date', timeDate.picker), field('Start (optional)', timeStart)),
          fieldRow(field('Minutes', timeMinutes), field('Rate (blank or 0 = rate rules)', timeRate.row)),
          field('Note', timeNote),
          checkLine(timeBillable),
          stackOf(buttonRow(), timeSave, timeCancel),
          muted('A rate of 0 resolves from the rate rules - weekend and after-hours from the chosen date and time.'),
          timeStatus.label
        )
      );

      // ---- Purchase ----
      const purchaseTicket = comboBox<string>([PICK_TICKET]);
      const catalogPicker = comboBox<string>([NO_CATALOG]);
      const priceExplanation = muted('');
      priceExplanation.visible = false;
      const purchaseDate = dateBox({ iso: todayLocalIso() });
      const purchaseDescription = textBox();
      const purchaseQuantity = textBox({ text: '1' });
      const purchaseCost = moneyBox({ placeholder: '0.00' });
      const purchasePrice = moneyBox({ placeholder: 'markup' });
      const purchaseStatus = new StatusLine();
      const purchaseSave = button('Add purchase', () => void savePurchase(), true);
      const purchaseCancel = button('Cancel', () => resetPurchaseEditor());
      purchaseCancel.visible = false;
      const purchaseCard = card();
      purchaseCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Purchase'),
          field('Ticket', purchaseTicket),
          field('From the catalog', catalogPicker),
          priceExplanation,
          fieldRow(field('Date', purchaseDate.picker), field('Quantity', purchaseQuantity)),
          field('Description', purchaseDescription),
          fieldRow(field('Unit cost', purchaseCost.row), field('Sell price (blank = markup)', purchasePrice.row)),
          stackOf(buttonRow(), purchaseSave, purchaseCancel),
          muted(
            "Leave sell blank to bill at cost plus this customer's purchase markup. Picking a catalog item fills these in; you can still change them for this one charge."
          ),
          purchaseStatus.label
        )
      );

      // ---- Lists ----
      const timeEmpty = muted('No time logged.');
      const timeList = new CardList<TimeEntryDto>({
        key: t => t.id,
        version: t => JSON.stringify(t),
        build: t => buildTimeRow(t),
        spacing: GAP.sm
      });
      const purchaseEmpty = muted('No purchases.');
      const purchaseList = new CardList<PurchaseDto>({
        key: p => p.id,
        version: p => JSON.stringify(p),
        build: p => buildPurchaseRow(p),
        spacing: GAP.sm
      });

      const invoiceStatus = new StatusLine();
      const generateButton = button('Generate invoice', () => void generate(), true);
      const invoiceEmpty = muted('No invoices yet.');
      const invoiceList = new CardList<InvoiceDto>({
        key: i => i.id,
        version: i => `${i.status}|${i.total}|${i.dueUtc}|${i.paidUtc}`,
        build: i =>
          invoiceCard(i, {
            onOpen: x => ctx.go(`/invoices/${x.id}`),
            onIssue: x => void invoiceAction(x, 'issue'),
            onMarkPaid: x => void invoiceAction(x, 'paid')
          }),
        spacing: GAP.sm
      });

      content.controls.add(
        stackOf(
          vstack({ spacing: GAP.lg }),
          rates,
          noTickets,
          heldCard,
          timerCard,
          timeCard,
          purchaseCard,
          stackOf(vstack({ spacing: GAP.sm }), h2('Time'), timeEmpty, timeList.panel),
          stackOf(vstack({ spacing: GAP.sm }), h2('Purchases'), purchaseEmpty, purchaseList.panel),
          stackOf(
            vstack({ spacing: GAP.sm }),
            h2('Invoices'),
            muted('Generating pulls in the unbilled work of every resolved or closed ticket, grouped by ticket.'),
            stackOf(buttonRow(), generateButton),
            invoiceStatus.label,
            invoiceEmpty,
            invoiceList.panel
          )
        )
      );

      // =================================================================
      //  Rows
      // =================================================================

      function rowActions(invoiced: boolean, onEdit: () => void, onDelete: () => void) {
        const edit = button('Edit', onEdit);
        const remove = button('Delete', onDelete);
        // Invoiced work is the invoice's now - edit it there.
        edit.enabled = remove.enabled = !invoiced;
        return stackOf(buttonRow(), edit, remove);
      }

      function buildTimeRow(t: TimeEntryDto): Card {
        const heading = buttonRow({ spacing: GAP.sm });
        heading.controls.add(value(t.notes?.trim() ? t.notes : '(no note)', { font: FONT.bold }));
        if (t.invoiceId !== null) heading.controls.add(chip('invoiced', 'neutral'));
        if (!t.billable) heading.controls.add(chip('not billable', 'neutral'));

        const left = stackOf(
          vstack({ spacing: GAP.xs }),
          heading,
          muted(`#${t.ticketId} ${t.ticketTitle}`),
          muted(
            join([
              localDate(t.startUtc),
              `${t.minutes} min (raw ${t.rawMinutes})`,
              `${RateKind[t.rateKind] ?? ''} @ ${money(t.rate)}/hr`,
              t.userName
            ])
          )
        );
        if (t.holdLabel?.trim()) left.controls.add(muted(t.holdLabel, 'warn'));
        left.controls.add(rowActions(t.invoiceId !== null, () => loadTime(t), () => void deleteTime(t)));

        const row = splitRow({ align: 'start', spacing: GAP.md });
        fillWith(row, left);
        row.controls.add(value(money(t.amount), { font: FONT.bold }));
        const shell = card();
        shell.controls.add(row);
        return shell;
      }

      function buildPurchaseRow(p: PurchaseDto): Card {
        const heading = buttonRow({ spacing: GAP.sm });
        heading.controls.add(value(p.description, { font: FONT.bold }));
        if (p.invoiceId !== null) heading.controls.add(chip('invoiced', 'neutral'));

        const left = stackOf(
          vstack({ spacing: GAP.xs }),
          heading,
          muted(`#${p.ticketId} ${p.ticketTitle}`),
          muted(join([localDate(p.purchaseDateUtc), `×${p.quantity} @ ${money(p.unitCost)} cost → ${p.priceLabel}`]))
        );
        if (p.holdLabel?.trim()) left.controls.add(muted(p.holdLabel, 'warn'));
        left.controls.add(rowActions(p.invoiceId !== null, () => loadPurchase(p), () => void deletePurchase(p)));

        const shell = card();
        shell.controls.add(left);
        return shell;
      }

      // =================================================================
      //  Pickers
      // =================================================================

      function ticketAt(picker: { selectedIndex: number }): TicketDto | null {
        return picker.selectedIndex > 0 ? tickets[picker.selectedIndex - 1] ?? null : null;
      }

      function selectTicket(picker: { selectedIndex: number }, id: number | null | undefined): void {
        const at = id == null ? -1 : tickets.findIndex(t => t.id === id);
        quiet = true;
        picker.selectedIndex = at + 1;
        quiet = false;
      }

      // The timer and the time editor share their ticket.
      timerTicket.on('selectionchanged', () => {
        if (!quiet) selectTicket(timeTicket, ticketAt(timerTicket)?.id);
        showButtons();
      });
      timeTicket.on('selectionchanged', () => {
        if (!quiet) selectTicket(timerTicket, ticketAt(timeTicket)?.id);
        showButtons();
      });
      purchaseTicket.on('selectionchanged', () => showButtons());
      catalogPicker.on('selectionchanged', () => {
        if (!quiet) void applyCatalog();
      });

      function showButtons(): void {
        const timeTicketPicked = ticketAt(timeTicket) !== null;
        startButton.enabled = timer === null && timeTicketPicked;
        stopButton.enabled = timer !== null;
        discardButton.enabled = timer !== null;
        timeSave.enabled = timeTicketPicked;
        purchaseSave.enabled = ticketAt(purchaseTicket) !== null;
      }

      async function applyCatalog(): Promise<void> {
        const item = catalogPicker.selectedIndex > 0 ? catalog[catalogPicker.selectedIndex - 1] : undefined;
        if (!item) {
          priceExplanation.visible = false;
          relayout(purchaseCard);
          return;
        }
        purchaseDescription.text = item.sku ? `${item.sku} - ${item.name}` : item.name;
        purchaseCost.set(item.unitCost);
        try {
          const quote = await crm.quoteCatalogItem(customerId, item.id, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          // A markup-derived price stays blank: blank means "work it out at invoice time", so a later
          // markup change still moves it. Typing the number in would freeze it.
          if (quote.source === CatalogPriceSource.CostPlusMarkup) purchasePrice.set(null);
          else purchasePrice.set(quote.unitPrice);
          priceExplanation.text = `${money(quote.unitPrice)} - ${quote.explanation}`;
          priceExplanation.visible = true;
        } catch (error) {
          if (ctx.cancelled()) return;
          priceExplanation.text = 'Could not price this item for this customer: ' + describeError(error);
          priceExplanation.visible = true;
        }
        relayout(purchaseCard);
      }

      // =================================================================
      //  Loading
      // =================================================================

      function showTimer(): void {
        timerText.text = timerLabel(timer, customerId);
        showButtons();
      }

      async function refreshTimer(): Promise<void> {
        try {
          timer = await crm.getRunningTimer(ctx.dispose.signal());
          if (!ctx.cancelled()) showTimer();
        } catch {
          // Offline for a moment: leave the label as it was rather than claim nothing is running.
        }
      }

      async function refresh(): Promise<void> {
        const keepTime = ticketAt(timeTicket)?.id;
        const keepPurchase = ticketAt(purchaseTicket)?.id;
        const keepCatalog = catalogPicker.selectedIndex > 0 ? catalog[catalogPicker.selectedIndex - 1]?.id : undefined;

        const signal = ctx.dispose.signal();
        const [t, e, p, i, c] = await Promise.all([
          crm.getTickets(customerId, signal),
          crm.getTimeEntries(customerId, signal),
          crm.getPurchases(customerId, signal),
          crm.getCustomerInvoices(customerId, signal),
          crm.getCatalog(false, signal)
        ]);
        if (ctx.cancelled()) return;

        tickets = sortTickets(t);
        entries = [...e].sort((a, b) => Date.parse(b.startUtc) - Date.parse(a.startUtc));
        purchases = [...p].sort((a, b) => Date.parse(b.purchaseDateUtc) - Date.parse(a.purchaseDateUtc));
        catalog = c;

        const labels = [PICK_TICKET, ...tickets.map(ticketLabel)];
        quiet = true;
        for (const picker of [timerTicket, timeTicket, purchaseTicket]) picker.items = labels;
        catalogPicker.items = [NO_CATALOG, ...catalog.map(x => (x.sku ? `${x.name} · ${x.sku}` : x.name))];
        catalogPicker.selectedIndex = keepCatalog === undefined ? 0 : catalog.findIndex(x => x.id === keepCatalog) + 1;
        quiet = false;
        selectTicket(timerTicket, keepTime);
        selectTicket(timeTicket, keepTime);
        selectTicket(purchaseTicket, keepPurchase);

        noTickets.visible = tickets.length === 0;
        heldText.text = heldWorkHint(entries, purchases, tickets);
        heldCard.visible = heldText.text.length > 0;

        timeList.set(entries);
        timeEmpty.visible = entries.length === 0;
        purchaseList.set(purchases);
        purchaseEmpty.visible = purchases.length === 0;
        const invoices = [...i].sort((a, b) => Date.parse(b.createdUtc) - Date.parse(a.createdUtc));
        invoiceList.set(invoices);
        invoiceEmpty.visible = invoices.length === 0;

        showButtons();
        relayout(content);
      }

      // =================================================================
      //  Timer
      // =================================================================

      async function startTimer(): Promise<void> {
        const t = ticketAt(timerTicket);
        if (!t) return;
        startButton.enabled = false;
        try {
          timer = await crm.startTimer(customerId, t.id, timerNote.text);
          if (ctx.cancelled()) return;
          timerNote.text = '';
          timerStatus.ok(`Timer started on #${t.id} ${t.title}.`);
        } catch (error) {
          if (!ctx.cancelled()) timerStatus.error(describeError(error));
        }
        showTimer();
      }

      async function stopTimer(): Promise<void> {
        stopButton.enabled = false;
        try {
          const entry = await crm.stopTimer(timerNote.text, timerBillable.checked);
          if (ctx.cancelled()) return;
          timer = null;
          timerNote.text = '';
          timerStatus.ok(`Logged ${entry.minutes} min (from ${entry.rawMinutes} raw) at ${money(entry.rate)}/hr.`);
          showTimer();
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) {
            timerStatus.error(describeError(error));
            showTimer();
          }
        }
      }

      async function discardTimer(): Promise<void> {
        const yes = await confirm(ctx.desktop, {
          title: 'Discard the timer',
          message: `Throw away the running timer${timer ? ` (${timer.elapsedMinutes} min)` : ''}? Nothing is logged.`
        });
        if (!yes || ctx.cancelled()) return;
        try {
          await crm.cancelTimer();
          if (ctx.cancelled()) return;
          timer = null;
          timerStatus.ok('Timer discarded.');
        } catch (error) {
          if (!ctx.cancelled()) timerStatus.error(describeError(error));
        }
        showTimer();
      }

      // =================================================================
      //  Time entries
      // =================================================================

      function loadTime(t: TimeEntryDto): void {
        editingTimeId = t.id;
        selectTicket(timeTicket, t.ticketId);
        selectTicket(timerTicket, t.ticketId);
        timeDate.set(localIsoOf(t.startUtc));
        timeStart.text = localTimeOfDay(t.startUtc);
        timeMinutes.text = String(t.rawMinutes > 0 ? t.rawMinutes : t.minutes);
        timeNote.text = t.notes ?? '';
        timeBillable.checked = t.billable;
        timeRate.set(t.rateKind === RateKind.Manual ? t.rate : null);
        timeSave.text = 'Save changes';
        timeCancel.visible = true;
        timeStatus.ok(`Editing time entry #${t.id}.`);
        showButtons();
        relayout(timeCard);
      }

      function resetTimeEditor(): void {
        editingTimeId = null;
        timeDate.set(todayLocalIso());
        timeStart.text = '';
        timeMinutes.text = '30';
        timeNote.text = '';
        timeBillable.checked = true;
        timeRate.set(null);
        timeSave.text = 'Add time';
        timeCancel.visible = false;
        timeStatus.clear();
        relayout(timeCard);
      }

      async function saveTime(): Promise<void> {
        const t = ticketAt(timeTicket);
        if (!t) return timeStatus.error('Pick the ticket this time belongs to.');
        const minutes = Number(timeMinutes.text.trim());
        if (!Number.isInteger(minutes) || minutes <= 0) return timeStatus.error('Enter minutes greater than zero.');
        const start = startUtc(timeDate.iso() || todayLocalIso(), timeStart.text);
        if (start === undefined) return timeStatus.error('The start time must be HH:mm, or left blank.');
        if (start === null) return timeStatus.error('Pick a date.');
        const rateText = timeRate.text().trim();
        const rate = rateText ? timeRate.value() : 0;
        if (rate === null || rate < 0) return timeStatus.error('The rate must be a number, or left blank to use the rate rules.');

        const body = {
          ticketId: t.id,
          startUtc: start,
          endUtc: null,
          minutes,
          rate,
          billable: timeBillable.checked,
          notes: timeNote.text
        };
        timeSave.enabled = false;
        timeStatus.busy('Saving…');
        try {
          if (editingTimeId !== null) {
            await crm.updateTimeEntry(editingTimeId, body);
            if (ctx.cancelled()) return;
            resetTimeEditor();
            timeStatus.ok('Time entry updated.');
          } else {
            const entry = await crm.createTimeEntry({ customerId, ...body });
            if (ctx.cancelled()) return;
            resetTimeEditor();
            timeStatus.ok(`Added ${entry.minutes} min at ${money(entry.rate)}/hr to ${t.title}.`);
          }
          await refresh();
        } catch (error) {
          // The editor stays open, so the entry is not retyped.
          if (!ctx.cancelled()) timeStatus.error(describeError(error));
        } finally {
          showButtons();
        }
      }

      async function deleteTime(t: TimeEntryDto): Promise<void> {
        const yes = await confirm(ctx.desktop, { title: 'Delete time', message: `Delete this time entry (${t.minutes} min)?` });
        if (!yes || ctx.cancelled()) return;
        try {
          await crm.deleteTimeEntry(t.id);
          if (ctx.cancelled()) return;
          if (editingTimeId === t.id) resetTimeEditor();
          timeStatus.ok('Time entry deleted.');
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) timeStatus.error('Could not delete it: ' + describeError(error));
        }
      }

      // =================================================================
      //  Purchases
      // =================================================================

      function loadPurchase(p: PurchaseDto): void {
        editingPurchaseId = p.id;
        selectTicket(purchaseTicket, p.ticketId);
        // The stored charge is the truth here: re-quoting would overwrite a price fixed when it was bought.
        quiet = true;
        catalogPicker.selectedIndex = p.catalogItemId === null ? 0 : catalog.findIndex(x => x.id === p.catalogItemId) + 1;
        quiet = false;
        priceExplanation.visible = false;
        purchaseDescription.text = p.description;
        purchaseQuantity.text = String(p.quantity);
        purchaseCost.set(p.unitCost);
        purchasePrice.set(p.unitPrice);
        purchaseDate.set(localIsoOf(p.purchaseDateUtc));
        purchaseSave.text = 'Save changes';
        purchaseCancel.visible = true;
        purchaseStatus.ok(`Editing purchase #${p.id}.`);
        showButtons();
        relayout(purchaseCard);
      }

      function resetPurchaseEditor(): void {
        editingPurchaseId = null;
        quiet = true;
        catalogPicker.selectedIndex = 0;
        quiet = false;
        priceExplanation.visible = false;
        purchaseDescription.text = '';
        purchaseQuantity.text = '1';
        purchaseCost.set(null);
        purchasePrice.set(null);
        purchaseDate.set(todayLocalIso());
        purchaseSave.text = 'Add purchase';
        purchaseCancel.visible = false;
        purchaseStatus.clear();
        relayout(purchaseCard);
      }

      async function savePurchase(): Promise<void> {
        const t = ticketAt(purchaseTicket);
        if (!t) return purchaseStatus.error('Pick the ticket this charge belongs to.');
        if (!purchaseDescription.text.trim()) return purchaseStatus.error('Enter a description.');
        const quantity = Number(purchaseQuantity.text.trim().replace(/,/g, ''));
        if (!Number.isFinite(quantity) || quantity <= 0) return purchaseStatus.error('Enter a quantity greater than zero.');
        const cost = purchaseCost.text().trim() ? purchaseCost.value() : 0;
        if (cost === null || cost < 0) return purchaseStatus.error('Enter a unit cost of zero or more.');
        const price = purchasePrice.text().trim() ? purchasePrice.value() : null;
        if (purchasePrice.text().trim() && (price === null || price < 0))
          return purchaseStatus.error("Enter a sell price of zero or more, or leave it blank to use the customer's markup.");
        const date = localNoonUtc(purchaseDate.iso() || todayLocalIso());
        if (!date) return purchaseStatus.error('Pick a date.');
        const catalogItemId = catalogPicker.selectedIndex > 0 ? catalog[catalogPicker.selectedIndex - 1]?.id ?? null : null;

        const body = {
          ticketId: t.id,
          description: purchaseDescription.text.trim(),
          quantity,
          unitCost: cost,
          billable: true,
          purchaseDateUtc: date,
          catalogItemId,
          unitPrice: price
        };
        purchaseSave.enabled = false;
        purchaseStatus.busy('Saving…');
        try {
          if (editingPurchaseId !== null) {
            await crm.updatePurchase(editingPurchaseId, body);
            if (ctx.cancelled()) return;
            resetPurchaseEditor();
            purchaseStatus.ok('Purchase updated.');
          } else {
            await crm.createPurchase({ customerId, ...body });
            if (ctx.cancelled()) return;
            resetPurchaseEditor();
            purchaseStatus.ok(`Purchase added to ${t.title}.`);
          }
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) purchaseStatus.error(describeError(error));
        } finally {
          showButtons();
        }
      }

      async function deletePurchase(p: PurchaseDto): Promise<void> {
        const yes = await confirm(ctx.desktop, { title: 'Delete purchase', message: `Delete purchase "${p.description}"?` });
        if (!yes || ctx.cancelled()) return;
        try {
          await crm.deletePurchase(p.id);
          if (ctx.cancelled()) return;
          if (editingPurchaseId === p.id) resetPurchaseEditor();
          purchaseStatus.ok('Purchase deleted.');
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) purchaseStatus.error('Could not delete it: ' + describeError(error));
        }
      }

      // =================================================================
      //  Invoices
      // =================================================================

      async function generate(): Promise<void> {
        generateButton.enabled = false;
        invoiceStatus.busy('Generating…');
        try {
          const invoice = await crm.generateInvoice(customerId);
          if (ctx.cancelled()) return;
          invoiceStatus.ok(`Created ${invoice.number} - ${money(invoice.total)}.`);
          await refresh();
        } catch (error) {
          // The refusal names the tickets that have to be resolved first; shown as the server wrote it.
          if (!ctx.cancelled()) invoiceStatus.error(describeError(error));
        } finally {
          generateButton.enabled = true;
        }
      }

      async function invoiceAction(i: InvoiceDto, action: 'issue' | 'paid'): Promise<void> {
        const yes = await confirm(ctx.desktop, {
          title: action === 'issue' ? 'Issue invoice' : 'Mark paid',
          message:
            action === 'issue'
              ? `Issue ${i.number} for ${money(i.total)}? Once issued its lines can still be corrected, but it counts as billed.`
              : `Mark ${i.number} (${money(i.total)}) as paid today?`
        });
        if (!yes || ctx.cancelled()) return;
        try {
          const updated = action === 'issue' ? await crm.issueInvoice(i.id) : await crm.markInvoicePaid(i.id);
          if (ctx.cancelled()) return;
          invoiceStatus.ok(`${updated.number} is now ${updated.statusLabel.toLowerCase()}.`);
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) invoiceStatus.error(describeError(error));
        }
      }

      // =================================================================

      void (async () => {
        try {
          const [customer, config] = await Promise.all([
            crm.getCustomer(customerId, ctx.dispose.signal()),
            crm.getBillingConfig(customerId, ctx.dispose.signal())
          ]);
          if (ctx.cancelled()) return;
          title.text = `Billing - ${customer.name}`;
          [rateLine1.text, rateLine2.text] = rateRulesLines(config);
          await Promise.all([refreshTimer(), refresh()]);
        } catch (error) {
          if (!ctx.cancelled()) pageStatus.error('Could not load billing: ' + describeError(error));
        }
        relayout(content);
      })();

      // The elapsed minutes come from the server, and a timer can be started or stopped from another tab.
      ctx.dispose.interval(() => void refreshTimer(), 15_000);
    }
  };
}
